// Building blocks for the shared policy screens and the product adapters'
// sections: one card style, labels above fields, one-line helpers and errors,
// chips, a validated list input. Tokens only (dark-mode safe), rem sizes,
// spacing on the 0.25/0.5/0.75/1/1.5rem scale (spec 8).
import React, { useEffect, useState } from "react";
import { Select } from "antd";
import { ArrowLeft, Ban, Check, ShieldCheck, CircleCheck } from "lucide-react";

import { AN_COLORS, AN_FONT, AN_MONO, anAlpha } from "../../design-system/tokens";

export const CONTENT_MAX = "60rem";
// Editor and detail pages: the main column plus the side panel.
export const PAGE_MAX = "76rem";
export const ASIDE_WIDTH = "21rem";
// The side panel sits beside the main column from this width up, else under it.
export const TWO_COLUMNS = "(min-width: 64rem)";

// Section titles sit beside their fields from this width up, which halves the scrolling.
const SIDE_LABELS = "(min-width: 56rem)";
export function useWide(query) {
  const get = () => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia(query).matches : false);
  const [wide, setWide] = useState(get);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const on = () => setWide(mq.matches);
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    return () => (mq.removeEventListener ? mq.removeEventListener("change", on) : mq.removeListener(on));
  }, [query]);
  return wide;
}

/** A section card: surface, 1px line, 0.5rem radius. */
export function Card({ id, title, help, aside, children, style }) {
  return (
    <section
      id={id}
      aria-labelledby={id ? `${id}-title` : undefined}
      style={{
        background: AN_COLORS.surface,
        border: `1px solid ${AN_COLORS.line}`,
        borderRadius: "0.5rem",
        padding: "1rem 1.5rem 1.5rem",
        fontFamily: AN_FONT,
        scrollMarginTop: "1rem",
        ...style,
      }}
    >
      {(title || aside) && (
        <div style={{ display: "flex", alignItems: "baseline", gap: "0.75rem", marginBottom: help ? "0.25rem" : "1rem" }}>
          {title && (
            <h2 id={id ? `${id}-title` : undefined} style={{ margin: 0, fontSize: "1rem", fontWeight: 600, color: AN_COLORS.ink }}>
              {title}
            </h2>
          )}
          {aside && <div style={{ marginLeft: "auto" }}>{aside}</div>}
        </div>
      )}
      {help && <p style={{ margin: "0 0 1rem", fontSize: "0.8125rem", color: AN_COLORS.muted, lineHeight: 1.5 }}>{help}</p>}
      <div style={{ display: "grid", gap: "1rem" }}>{children}</div>
    </section>
  );
}

/**
 * One part of the editor's basics card (Policy name, Who, Where, Action): a
 * small heading, the body, and a line above every part but the first. A
 * region named by its title.
 */
export function SubSection({ id, title, children, first }) {
  const wide = useWide(SIDE_LABELS);
  return (
    <section
      id={id}
      aria-labelledby={id ? `${id}-title` : undefined}
      style={{
        display: "grid",
        gridTemplateColumns: wide ? "10rem minmax(0, 1fr)" : "minmax(0, 1fr)",
        gap: wide ? "1.5rem" : "0.75rem",
        padding: "1.25rem 1.5rem",
        borderTop: first ? "none" : `1px solid ${AN_COLORS.line2}`,
        scrollMarginTop: "1rem",
        minWidth: 0,
      }}
    >
      <h2
        id={id ? `${id}-title` : undefined}
        style={{ margin: 0, paddingTop: wide ? "0.375rem" : 0, fontSize: "0.9375rem", fontWeight: 600, color: AN_COLORS.ink }}
      >
        {title}
      </h2>
      <div style={{ display: "grid", gap: "1rem", minWidth: 0 }}>{children}</div>
    </section>
  );
}

/** A titled group inside the Advanced block (Conditions, Except, Approval, Session). */
export function Group({ id, title, help, aside, children }) {
  return (
    <div id={id} role="group" aria-labelledby={id ? `${id}-title` : undefined} style={{ display: "grid", gap: "0.75rem", minWidth: 0, scrollMarginTop: "1rem" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: "0.75rem" }}>
        <h3 id={id ? `${id}-title` : undefined} style={{ margin: 0, fontSize: "0.875rem", fontWeight: 600, color: AN_COLORS.ink }}>
          {title}
        </h3>
        {aside && <div style={{ marginLeft: "auto" }}>{aside}</div>}
      </div>
      {help && <div style={{ marginTop: "-0.5rem", fontSize: "0.8125rem", color: AN_COLORS.muted, lineHeight: 1.5 }}>{help}</div>}
      {children}
    </div>
  );
}

export const ErrorText = ({ children, id }) =>
  children ? (
    <div id={id} role="alert" style={{ fontSize: "0.75rem", color: AN_COLORS.danger, marginTop: "0.25rem", lineHeight: 1.45 }}>
      {children}
    </div>
  ) : null;

export const HintText = ({ children, style }) =>
  children ? (
    <div style={{ fontSize: "0.75rem", color: AN_COLORS.muted, marginTop: "0.25rem", lineHeight: 1.5, ...style }}>{children}</div>
  ) : null;

/** Label above the field, optional muted hint after it, then the error. */
export function Field({ label, hint, help, error, required, children, htmlFor, style }) {
  return (
    <div style={style}>
      {label && (
        <label
          htmlFor={htmlFor}
          style={{ display: "block", fontSize: "0.8125rem", fontWeight: 500, color: AN_COLORS.ink2, marginBottom: "0.375rem" }}
        >
          {label}
          {required && <span aria-hidden style={{ color: AN_COLORS.danger, marginLeft: "0.25rem" }}>*</span>}
          {hint && <span style={{ color: AN_COLORS.muted, fontWeight: 400, marginLeft: "0.5rem" }}>{hint}</span>}
        </label>
      )}
      {children}
      {error ? <ErrorText>{error}</ErrorText> : <HintText>{help}</HintText>}
    </div>
  );
}

export const Muted = ({ children, style }) => (
  <span style={{ color: AN_COLORS.muted, fontSize: "0.8125rem", lineHeight: 1.5, ...style }}>{children}</span>
);

/** Typed things only: IPs, hostnames, accounts, SPNs. */
export const Mono = ({ children }) => <span style={{ fontFamily: AN_MONO, fontSize: "0.8125rem" }}>{children}</span>;

const NOTE_TONES = {
  warn: { bg: "var(--an-warnBg)", bd: "var(--an-warnBd)", fg: "var(--an-warnFg)" },
  danger: { bg: "var(--an-dangerBg)", bd: "var(--an-dangerBd)", fg: "var(--an-dangerFg)" },
  info: { bg: "var(--an-infoBg)", bd: "var(--an-infoBd)", fg: "var(--an-infoFg)" },
};

/** The one info box a section may have. */
export function Note({ tone = "info", children, action, role }) {
  const t = NOTE_TONES[tone] || NOTE_TONES.info;
  return (
    <div
      role={role || (tone === "danger" ? "alert" : undefined)}
      style={{
        display: "flex",
        gap: "0.75rem",
        alignItems: "flex-start",
        padding: "0.75rem 1rem",
        borderRadius: "0.5rem",
        background: t.bg,
        border: `1px solid ${t.bd}`,
        color: t.fg,
        fontSize: "0.8125rem",
        lineHeight: 1.5,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
      {action}
    </div>
  );
}

/** A text button (links inside copy, "How conditions work"). */
export function TextButton({ children, onClick, tone, style, ...rest }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: "none",
        background: "transparent",
        padding: 0,
        cursor: "pointer",
        color: tone === "danger" ? AN_COLORS.danger : AN_COLORS.primary,
        fontFamily: AN_FONT,
        fontSize: "0.8125rem",
        fontWeight: 500,
        ...style,
      }}
      {...rest}
    >
      {children}
    </button>
  );
}

/** Toggle chips over a fixed vocabulary. value: string[]. Selected = brand border and text, no fill. */
export function Chips({ options, value, onChange, ariaLabel, disabled, single }) {
  const selected = new Set((value || []).map((v) => String(v).toLowerCase()));
  const toggle = (v) => {
    if (single) return onChange([v]);
    return onChange(selected.has(v) ? (value || []).filter((x) => String(x).toLowerCase() !== v) : [...(value || []), v]);
  };
  return (
    <div role="group" aria-label={ariaLabel} style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
      {options.map((o) => {
        const on = selected.has(o.value);
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={on}
            disabled={disabled}
            onClick={() => toggle(o.value)}
            style={{
              padding: "0.25rem 0.75rem",
              borderRadius: "1rem",
              border: `1px solid ${on ? AN_COLORS.primary : AN_COLORS.line}`,
              background: AN_COLORS.surface,
              color: on ? AN_COLORS.primary : AN_COLORS.ink2,
              fontFamily: AN_FONT,
              fontSize: "0.8125rem",
              fontWeight: on ? 600 : 500,
              cursor: disabled ? "not-allowed" : "pointer",
              opacity: disabled ? 0.55 : 1,
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

const SEG_TONE = {
  neutral: () => ({ fg: AN_COLORS.ink, bd: AN_COLORS.ink3, bg: anAlpha(AN_COLORS.ink, 6) }),
  brand: () => ({ fg: AN_COLORS.primary, bd: AN_COLORS.primary, bg: anAlpha(AN_COLORS.primary, 10) }),
  danger: () => ({ fg: AN_COLORS.danger, bd: AN_COLORS.danger, bg: anAlpha(AN_COLORS.danger, 10) }),
};

/** One-of segmented control; the only place a selected option is tinted. options: [{ value, label, tone }]. */
export function Segmented({ options, value, onChange, ariaLabel }) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      style={{ display: "inline-flex", justifySelf: "start", width: "fit-content", border: `1px solid ${AN_COLORS.line}`, borderRadius: "0.5rem", padding: "0.25rem", gap: "0.25rem", flexWrap: "wrap" }}
    >
      {options.map((o) => {
        const on = o.value === value;
        const t = (SEG_TONE[o.tone] || SEG_TONE.neutral)();
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.value)}
            style={{
              padding: "0.375rem 1rem",
              borderRadius: "0.375rem",
              border: `1px solid ${on ? t.bd : "transparent"}`,
              background: on ? t.bg : "transparent",
              color: on ? t.fg : AN_COLORS.ink2,
              fontFamily: AN_FONT,
              fontSize: "0.875rem",
              fontWeight: on ? 600 : 500,
              cursor: "pointer",
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * A list of typed entries (IPs, SPNs, hosts), validated as typed: an entry
 * `validate` refuses is not added and the message says why.
 *   validate(entry) -> true | "message"
 *   normalise(entry) -> stored form (optional)
 */
export function TokenInput({ value, onChange, validate, normalise, placeholder, onBlur, ariaLabel, status, id, mono = true, separators }) {
  const [bad, setBad] = useState("");
  const change = (next) => {
    const entries = next.map((v) => String(v).trim()).filter(Boolean).map((v) => (normalise ? normalise(v) : v));
    const results = entries.map((v) => ({ v, ok: validate ? validate(v) : true }));
    const refused = results.filter((r) => r.ok !== true);
    setBad(refused.length ? (typeof refused[0].ok === "string" ? refused[0].ok : `${refused[0].v} isn't valid.`) : "");
    onChange(results.filter((r) => r.ok === true).map((r) => r.v));
  };
  return (
    <div>
      <Select
        id={id}
        mode="tags"
        aria-label={ariaLabel}
        value={value || []}
        onChange={change}
        onBlur={onBlur}
        open={false}
        suffixIcon={null}
        tokenSeparators={separators || [",", " ", "\n"]}
        placeholder={placeholder}
        status={bad || status ? "error" : undefined}
        style={{ width: "100%", fontFamily: mono ? AN_MONO : AN_FONT }}
      />
      <ErrorText>{bad}</ErrorText>
    </div>
  );
}

/* ----------------------------------------------- the redesigned page pieces */

// Each action's colour, icon and one-line meaning: the editor's action cards,
// the list's pills, the detail page's Then tile.
export const ACTION_META = {
  allow: { label: "Allow", icon: CircleCheck, fg: "var(--an-successFg)", bg: "var(--an-successBg)", bd: "var(--an-successBd)", solid: AN_COLORS.success },
  mfa_required: { label: "Require MFA", icon: ShieldCheck, fg: "var(--an-infoFg)", bg: "var(--an-infoBg)", bd: "var(--an-infoBd)", solid: AN_COLORS.primary },
  block: { label: "Deny", icon: Ban, fg: "var(--an-dangerFg)", bg: "var(--an-dangerBg)", bd: "var(--an-dangerBd)", solid: AN_COLORS.danger },
};
export const actionMeta = (flow) => ACTION_META[flow] || ACTION_META.allow;

/** A small coloured pill for an action ("Require MFA"), optionally "· fallback". */
export function ActionPill({ flow, fallback, style }) {
  const m = actionMeta(flow);
  const Icon = m.icon;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.3125rem",
        padding: "0.125rem 0.5rem",
        borderRadius: "1rem",
        background: m.bg,
        border: `1px solid ${m.bd}`,
        color: m.fg,
        fontSize: "0.75rem",
        fontWeight: 600,
        whiteSpace: "nowrap",
        lineHeight: 1.5,
        ...style,
      }}
    >
      <Icon size={12} aria-hidden />
      {m.label}
      {fallback && <span style={{ fontWeight: 500, opacity: 0.8 }}>· fallback</span>}
    </span>
  );
}

/** A quiet rounded tag (a condition, "Paused"). */
export function Tag({ children, tone, title }) {
  const fg = tone === "warn" ? "var(--an-warnFg)" : AN_COLORS.ink3;
  const bg = tone === "warn" ? "var(--an-warnBg)" : AN_COLORS.tint;
  return (
    <span
      title={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.25rem",
        padding: "0 0.4375rem",
        borderRadius: "0.3125rem",
        background: bg,
        border: `1px solid ${AN_COLORS.line}`,
        color: fg,
        fontSize: "0.6875rem",
        fontWeight: 500,
        lineHeight: "1.25rem",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

/**
 * The page header: a square back button, the breadcrumb, the title, a status
 * line under it and the page's actions on the right.
 */
export function PageTitleBar({ onBack, backLabel, crumbs = [], title, status, actions }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: "1rem", flexWrap: "wrap" }}>
      <button
        type="button"
        onClick={onBack}
        aria-label={backLabel}
        title={backLabel}
        className="an-policy-back"
        style={{
          width: "2.5rem",
          height: "2.5rem",
          flexShrink: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "0.625rem",
          border: `1px solid ${AN_COLORS.line}`,
          background: AN_COLORS.surface,
          color: AN_COLORS.ink,
          cursor: "pointer",
          marginTop: "0.125rem",
        }}
      >
        <ArrowLeft size={18} aria-hidden />
      </button>
      <div style={{ flex: "1 1 18rem", minWidth: 0 }}>
        {crumbs.length > 0 && (
          <nav aria-label="Breadcrumb" style={{ display: "flex", gap: "0.375rem", alignItems: "center", fontSize: "0.8125rem", color: AN_COLORS.muted, flexWrap: "wrap" }}>
            {crumbs.map((c, i) => (
              <React.Fragment key={c.label}>
                {i > 0 && <span aria-hidden>/</span>}
                {c.onClick ? (
                  <TextButton onClick={c.onClick} style={{ color: AN_COLORS.muted, fontWeight: 400 }}>
                    {c.label}
                  </TextButton>
                ) : (
                  <span style={{ color: AN_COLORS.ink3 }}>{c.label}</span>
                )}
              </React.Fragment>
            ))}
          </nav>
        )}
        <h1 style={{ margin: "0.125rem 0 0", fontSize: "1.5rem", fontWeight: 650, letterSpacing: "-0.02em", color: AN_COLORS.ink, overflowWrap: "anywhere", lineHeight: 1.25 }}>
          {title}
        </h1>
        {status && <div style={{ marginTop: "0.375rem", display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>{status}</div>}
      </div>
      {actions && <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap", marginLeft: "auto" }}>{actions}</div>}
    </div>
  );
}

/** A coloured dot and a word ("Draft · not saved yet", "On"). */
export function StatusDot({ tone = "muted", children }) {
  const color = { success: AN_COLORS.success, warn: AN_COLORS.warn, danger: AN_COLORS.danger, brand: AN_COLORS.primary }[tone] || AN_COLORS.muted;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "0.375rem", fontSize: "0.8125rem", color: AN_COLORS.muted }}>
      <span aria-hidden style={{ width: "0.5rem", height: "0.5rem", borderRadius: "50%", background: color }} />
      {children}
    </span>
  );
}

/** A plain surface card for the side panel and the detail page. */
export function Panel({ title, children, style, id, label }) {
  return (
    <section
      id={id}
      aria-label={label || title}
      style={{
        background: AN_COLORS.surface,
        border: `1px solid ${AN_COLORS.line}`,
        borderRadius: "0.75rem",
        padding: "1.125rem 1.25rem",
        fontFamily: AN_FONT,
        minWidth: 0,
        ...style,
      }}
    >
      {title && <Eyebrow style={{ marginBottom: "0.75rem" }}>{title}</Eyebrow>}
      {children}
    </section>
  );
}

/** Small uppercase label ("THIS POLICY WILL", "WHO"). */
export const Eyebrow = ({ children, style }) => (
  <div style={{ fontFamily: AN_MONO, fontSize: "0.6875rem", letterSpacing: "0.08em", textTransform: "uppercase", color: AN_COLORS.muted, ...style }}>{children}</div>
);

/**
 * One numbered step of the editor: a badge (the number, or a check once the
 * step is complete), the question it answers, then the fields. A region
 * named by its title.
 */
export function StepCard({ id, step, title, help, done, optional, aside, error, children }) {
  return (
    <section
      id={id}
      aria-labelledby={id ? `${id}-title` : undefined}
      style={{
        background: AN_COLORS.surface,
        border: `1px solid ${error ? AN_COLORS.danger : AN_COLORS.line}`,
        borderRadius: "0.75rem",
        padding: "1.25rem 1.5rem 1.5rem",
        fontFamily: AN_FONT,
        scrollMarginTop: "1rem",
        minWidth: 0,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: "0.875rem", marginBottom: "1.125rem" }}>
        <span
          aria-hidden
          style={{
            width: "1.75rem",
            height: "1.75rem",
            flexShrink: 0,
            borderRadius: "50%",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "0.8125rem",
            fontWeight: 700,
            background: done ? AN_COLORS.primary : AN_COLORS.tint,
            color: done ? "#fff" : AN_COLORS.ink3,
            border: `1px solid ${done ? AN_COLORS.primary : AN_COLORS.line}`,
          }}
        >
          {done ? <Check size={14} strokeWidth={3} /> : step}
        </span>
        <div style={{ flex: 1, minWidth: 0, paddingTop: "0.125rem" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: "0.5rem", flexWrap: "wrap" }}>
            <h2 id={id ? `${id}-title` : undefined} style={{ margin: 0, fontSize: "1rem", fontWeight: 650, color: AN_COLORS.ink, letterSpacing: "-0.01em" }}>
              {title}
            </h2>
            {optional && <span style={{ fontSize: "0.75rem", color: AN_COLORS.muted }}>Optional</span>}
          </div>
          {help && <div style={{ marginTop: "0.1875rem", fontSize: "0.8125rem", color: AN_COLORS.muted, lineHeight: 1.5 }}>{help}</div>}
        </div>
        {aside && <div style={{ marginLeft: "auto", flexShrink: 0 }}>{aside}</div>}
      </div>
      <div style={{ display: "grid", gap: "1.125rem", minWidth: 0, paddingLeft: "2.625rem" }} className="an-step-body">
        {children}
      </div>
    </section>
  );
}

/** A dashed "+ Something" button (add a condition, add an exception). */
export function AddButton({ children, onClick, disabled, ariaLabel }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.375rem",
        padding: "0.4375rem 0.75rem",
        borderRadius: "0.5rem",
        border: `1px dashed ${AN_COLORS.line}`,
        background: "transparent",
        color: disabled ? AN_COLORS.muted : AN_COLORS.ink2,
        fontFamily: AN_FONT,
        fontSize: "0.8125rem",
        fontWeight: 500,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {children}
    </button>
  );
}
